import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { makeStyles } from "@material-ui/core/styles";
import FolderIcon from "@material-ui/icons/Folder";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FilterAltOffOutlinedIcon from "@mui/icons-material/FilterAltOffOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { frFR } from "@mui/x-data-grid/locales";

import useSmqPermissions from "../../../hooks/useSmqPermissions";
import SMQMainContainer from "../../../components/SMQMainContainer";
import ProcessusService from "../../../services/smq/ProcessusService";
import ButtonYellow from "../component/BouttonYellow";
import useParameter from "../hooks/useParameter";
import HeaderProcessus from "./HeaderProcessus";
import ReferentielAdd from "./ReferentielAdd";

const useStyles = makeStyles(() => ({
  folderIcon: {
    color: "#d4a733",
    flexShrink: 0,
    fontSize: 15,
  },
}));

const CATEGORY_COLORS = {
  controle: { backgroundColor: "#eee6ff", color: "#6f35c5" },
  direction: { backgroundColor: "#e7eff7", color: "#173d61" },
  realisation: { backgroundColor: "#e4f2e9", color: "#28724a" },
  support: { backgroundColor: "#faeadb", color: "#b45c12" },
};

const normalizeCategoryName = (name = "") =>
  name
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("fr");

const getCategoryColors = (name) =>
  CATEGORY_COLORS[normalizeCategoryName(name)] || {
    backgroundColor: "#edf1f5",
    color: "#52677c",
  };

const getCategoryKey = (id, name) =>
  id === null || id === undefined
    ? `name:${normalizeCategoryName(name)}`
    : `id:${String(id)}`;

const EMPTY_DIALOG = {
  mode: "create",
  open: false,
  row: null,
};

const KpiCard = ({ title, value }) => (
    <Paper
        elevation={0}
        sx={{
            backgroundColor: "#f6f9fc",
            border: "1px solid #cbd8e6",
            borderRadius: "8px",
            p: 2,
            width: { xs: "50%", sm: 180 },
            // textAlign: "center",
        }}
    >
        <Typography color="#60758a" variant="body2">
            {title}
        </Typography>
        <Typography color="#10263d" fontWeight={700} variant="h6">
            {value}
        </Typography>
    </Paper>
);

const Referentiel = () => {
  const { isAdmin: isSmqAdmin } = useSmqPermissions();
  const classes = useStyles();
  const [referentiels, setReferentiels] = useState([]);
  const [referentielsLoading, setReferentielsLoading] = useState(true);
  const [referentielsError, setReferentielsError] = useState(null);
  const [formDialog, setFormDialog] = useState(EMPTY_DIALOG);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [filtersVisible, setFiltersVisible] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [notification, setNotification] = useState({
    message: "",
    open: false,
    severity: "success",
  });

  const {
    categories,
    categoriesError,
    categoriesLoading,
    refreshCategories,
  } = useParameter();

  const categoryOptions = useMemo(
    () =>
      categories.map((category) => ({
        label: category.categorie_name,
        value: category.categorie_id,
      })),
    [categories]
  );

  const rows = useMemo(
    () =>
      referentiels.map((referentiel) => ({
        categorie_id: referentiel.categorie_id,
        categorie_name: referentiel.categorie_name,
        code: referentiel.referentiel_code,
        id: referentiel.referentiel_id,
        name: referentiel.referentiel_nom,
      })),
    [referentiels]
  );

  const existingIds = useMemo(() => rows.map((row) => row.code), [rows]);

  const orderedCategoryKpis = useMemo(() => {
    const counts = new Map();

    referentiels.forEach((referentiel) => {
      const key = getCategoryKey(
        referentiel.categorie_id,
        referentiel.categorie_name
      );
      const current = counts.get(key) || {
        title: referentiel.categorie_name || "Non renseignée",
        value: 0,
      };
      counts.set(key, { ...current, value: current.value + 1 });
    });

    const sortedCategories = categories
      .map((category, index) => ({ category, index }))
      .sort((left, right) => {
        const leftValue = left.category.categorie_ordre;
        const rightValue = right.category.categorie_ordre;
        const leftOrder = Number(leftValue);
        const rightOrder = Number(rightValue);
        const safeLeftOrder =
          leftValue !== null && leftValue !== undefined && leftValue !== "" &&
          Number.isFinite(leftOrder)
            ? leftOrder
            : Number.MAX_SAFE_INTEGER;
        const safeRightOrder =
          rightValue !== null &&
          rightValue !== undefined &&
          rightValue !== "" &&
          Number.isFinite(rightOrder)
            ? rightOrder
            : Number.MAX_SAFE_INTEGER;

        return safeLeftOrder - safeRightOrder || left.index - right.index;
      });
    const orderedKeys = new Set();
    const orderedKpis = sortedCategories.reduce((result, { category }) => {
      const key = getCategoryKey(
        category.categorie_id,
        category.categorie_name
      );
      orderedKeys.add(key);
      const count = counts.get(key);

      if (count) {
        result.push({
          key,
          title: category.categorie_name,
          value: count.value,
        });
      }

      return result;
    }, []);

    counts.forEach((kpi, key) => {
      if (!orderedKeys.has(key)) {
        orderedKpis.push({ key, ...kpi });
      }
    });

    return orderedKpis;
  }, [categories, referentiels]);

  const filteredRows = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLocaleLowerCase("fr");

    return rows.filter((row) => {
      const matchesSearch =
        !normalizedSearch ||
        `${row.code || ""} ${row.name || ""}`
          .toLocaleLowerCase("fr")
          .includes(normalizedSearch);
      const matchesCategory =
        categoryFilter === "" ||
        String(row.categorie_id) === String(categoryFilter);

      return matchesSearch && matchesCategory;
    });
  }, [categoryFilter, rows, searchTerm]);

  const hasActiveFilters = searchTerm !== "" || categoryFilter !== "";

  const resetFilters = useCallback(() => {
    setSearchTerm("");
    setCategoryFilter("");
  }, []);

  const showNotification = useCallback((message, severity = "success") => {
    setNotification({ message, open: true, severity });
  }, []);

  const loadReferentiels = useCallback(async () => {
    setReferentielsLoading(true);
    setReferentielsError(null);

    try {
      const response = await ProcessusService.getReferentiels();
      if (!Array.isArray(response?.data)) {
        throw new Error(
          "Le service des référentiels a retourné une réponse invalide"
        );
      }
      setReferentiels(response.data);
    } catch (error) {
      setReferentielsError(error.message);
    } finally {
      setReferentielsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReferentiels();
  }, [loadReferentiels]);

  useEffect(() => {
    setPaginationModel((current) => ({ ...current, page: 0 }));
  }, [categoryFilter, searchTerm]);

  const openCreateDialog = useCallback(() => {
    if (!isSmqAdmin) return;
    setFormDialog({ mode: "create", open: true, row: null });
  }, [isSmqAdmin]);

  const openEditDialog = useCallback((row) => {
    if (!isSmqAdmin) return;
    setFormDialog({ mode: "edit", open: true, row });
  }, [isSmqAdmin]);

  const closeFormDialog = useCallback(() => {
    setFormDialog(EMPTY_DIALOG);
  }, []);

  const handleSaved = useCallback(async () => {
    const successMessage =
      formDialog.mode === "edit"
        ? "Référentiel modifié avec succès"
        : "Référentiel créé avec succès";
    showNotification(successMessage);
    await loadReferentiels();
  }, [formDialog.mode, loadReferentiels, showNotification]);

  const handleDelete = async () => {
    if (!isSmqAdmin || !deleteTarget || deleting) return;

    setDeleting(true);
    try {
      await ProcessusService.deleteReferentiel(deleteTarget.id);
      setReferentiels((current) =>
        current.filter(
          (referentiel) =>
            referentiel.referentiel_id !== deleteTarget.id
        )
      );
      setDeleteTarget(null);
      showNotification("Référentiel supprimé avec succès");
      await loadReferentiels();
    } catch (error) {
      showNotification(error.message, "error");
    } finally {
      setDeleting(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        field: "code",
        flex: 0.7,
        headerName: "ID unique",
        minWidth: 130,
        renderCell: ({ value }) => (
          <Box
            component="span"
            sx={{
              backgroundColor: "#edf3f8",
              borderRadius: "4px",
              color: "#173d61",
              fontFamily: "'DM Mono', monospace",
              fontSize: 11,
              fontWeight: 700,
              px: 0.8,
              py: 0.35,
            }}
          >
            {value}
          </Box>
        ),
      },
      {
        field: "name",
        flex: 2.8,
        headerName: "Nom complet",
        minWidth: 260,
      },
      {
        field: "categorie_name",
        flex: 0.9,
        headerName: "Catégorie",
        minWidth: 145,
        renderCell: ({ value }) => (
          <Box
            component="span"
            sx={{
              ...getCategoryColors(value),
              borderRadius: "999px",
              fontSize: 10,
              fontWeight: 700,
              px: 1,
              py: 0.35,
            }}
          >
            {value || "Non renseignée"}
          </Box>
        ),
      },
      {
        align: "center",
        field: "actions",
        filterable: false,
        headerAlign: "center",
        headerName: "Actions",
        minWidth: 120,
        sortable: false,
        width: 120,
        renderCell: ({ row }) => isSmqAdmin ? (
          <Box sx={{ display: "flex", gap: 0.75 }}>
            <Tooltip title="Modifier">
              <IconButton
                aria-label={`Modifier ${row.code}`}
                onClick={() => openEditDialog(row)}
                size="small"
                sx={{
                  backgroundColor: "rgba(23, 61, 97, 0.1)",
                  borderRadius: "8px",
                  color: "#173d61",
                  height: 34,
                  width: 34,
                  "&:hover": { backgroundColor: "rgba(23, 61, 97, 0.18)" },
                }}
              >
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Supprimer">
              <IconButton
                aria-label={`Supprimer ${row.code}`}
                onClick={() => setDeleteTarget(row)}
                size="small"
                sx={{
                  backgroundColor: "rgba(173, 31, 36, 0.1)",
                  borderRadius: "8px",
                  color: "#ad1f24",
                  height: 34,
                  width: 34,
                  "&:hover": { backgroundColor: "rgba(173, 31, 36, 0.18)" },
                }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ) : null,
      },
    ],
    [isSmqAdmin, openEditDialog]
  );

  const editInitialValues = formDialog.row
    ? {
        category: formDialog.row.categorie_id,
        id: formDialog.row.code,
        name: formDialog.row.name,
        referentielId: formDialog.row.id,
      }
    : undefined;

  return (
    <SMQMainContainer>
      <>
        <HeaderProcessus
          icon={
            <FolderIcon
              aria-hidden="true"
              className={classes.folderIcon}
            />
          }
          title="Référentiel processus"
        />

        <Box sx={{ px: { xs: 1.5, sm: 2.5 }, pb: 3 }}>
          <Grid
            alignItems="center"
            container
            justifyContent="space-between"
            spacing={2}
          >
            <Grid item sm={6} xs={12}>
              <Typography
                color="#10263d"
                fontWeight={700}
                gutterBottom
                variant="h6"
              >
                Paramétrage des Processus
              </Typography>
              <Typography color="#60758a" variant="body2">
                Table de référence unique
              </Typography>
            </Grid>
            <Grid
              item
              sm={6}
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                justifyContent: { xs: "flex-start", sm: "flex-end" },
              }}
              xs={12}
            >
              <Button
                color="inherit"
                onClick={() => setFiltersVisible((visible) => !visible)}
                size="small"
                startIcon={
                  filtersVisible ? (
                    <FilterAltOffOutlinedIcon />
                  ) : (
                    <FilterAltOutlinedIcon />
                  )
                }
                sx={{
                  borderColor: "#9fb1c3",
                  color: "#173d61",
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "#f3f7fa",
                    borderColor: "#173d61",
                  },
                }}
                variant="outlined"
              >
                {filtersVisible ? "Masquer filtres" : "Afficher filtres"}
              </Button>
              {isSmqAdmin && (
                <ButtonYellow onClick={openCreateDialog} children="+  Créer un référentiel" />
              )}
            </Grid>
          </Grid>

          {referentielsError && (
            <Alert
              action={
                <Button color="inherit" onClick={loadReferentiels} size="small">
                  Réessayer
                </Button>
              }
              severity="error"
              sx={{ mt: 2 }}
            >
              Il y a eu un problème lors du chargement des référentiels
            </Alert>
          )}
          {filtersVisible && (
            <>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 2,
                  p: 2,
                }}
              >
                <KpiCard
                  title="Total de référentiels"
                  value={referentiels.length}
                />
                {orderedCategoryKpis.map((kpi) => (
                  <KpiCard
                    key={kpi.key}
                    title={kpi.title}
                    value={kpi.value}
                  />
                ))}
              </Box>

          <Paper
            elevation={0}
            sx={{
              alignItems: { sm: "center" },
              border: "1px solid #cbd8e6",
              borderRadius: "8px",
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 1,
              mt: 1,
              p: 1.25,
            }}
          >
            <Typography
              color="#52677c"
              fontSize={10}
              fontWeight={700}
              letterSpacing="0.05em"
              sx={{ flexShrink: 0, mr: 0.25 }}
            >
              FILTRES :
            </Typography>

            <TextField
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#62a5bd", fontSize: 17 }} />
                  </InputAdornment>
                ),
              }}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Rechercher ID ou nom..."
              size="small"
              sx={{
                width: { xs: "100%", sm: 400 },
                "& .MuiOutlinedInput-root": {
                  borderRadius: "6px",
                  fontSize: 12,
                  height: 36,
                },
              }}
              value={searchTerm}
            />

            <TextField
              onChange={(event) => setCategoryFilter(event.target.value)}
              select
              size="small"
              sx={{
                width: { xs: "100%", sm: 400 },
                "& .MuiOutlinedInput-root": {
                  borderRadius: "6px",
                  fontSize: 12,
                  height: 36,
                },
              }}
              value={categoryFilter}
            >
              <MenuItem value="">Toutes catégories</MenuItem>
              {categoryOptions.map((category) => (
                <MenuItem key={category.value} value={category.value}>
                  {category.label}
                </MenuItem>
              ))}
            </TextField>

            <Box
              sx={{
                alignItems: "center",
                display: "flex",
                gap: 1.5,
                justifyContent: { xs: "space-between", sm: "flex-end" },
                ml: { sm: "auto" },
                width: { xs: "100%", sm: "auto" },
              }}
            >
              <Typography color="#60758a" fontSize={11} whiteSpace="nowrap">
                {filteredRows.length} entrées / {rows.length} total
              </Typography>
              <Button
                color="inherit"
                disabled={!hasActiveFilters}
                onClick={resetFilters}
                size="small"
                startIcon={<RestartAltIcon />}
                sx={{
                  borderColor: "#9fb1c3",
                  color: "#173d61",
                  fontSize: 11,
                  fontWeight: 700,
                  minWidth: 118,
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "#f3f7fa",
                    borderColor: "#173d61",
                  },
                }}
                variant="outlined"
              >
                Réinitialiser
              </Button>
            </Box>
          </Paper>
            </>
          )}

          <Paper
            elevation={0}
            sx={{
              border: "1px solid #cbd8e6",
              borderRadius: "8px",
              height: 560,
              mt: 2,
              overflow: "hidden",
            }}
          >


            <DataGrid
              columns={columns}
              disableRowSelectionOnClick
              loading={referentielsLoading}
              localeText={
                frFR.components.MuiDataGrid.defaultProps.localeText
              }
              pageSizeOptions={[10, 25, 50]}
              onPaginationModelChange={setPaginationModel}
              paginationModel={paginationModel}
              rows={filteredRows}
              rowHeight={48}
              sx={{
                border: 0,
                color: "#10263d",
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 12,
                "& .MuiDataGrid-cell": {
                  borderColor: "#d5e0eb",
                },
                "& .MuiDataGrid-columnHeader": {
                  backgroundColor: "#173d61",
                  color: "#fff",
                  fontSize: 11,
                  fontWeight: 700,
                },
                "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus": {
                  outline: "2px solid #d5ad3f",
                  outlineOffset: "-2px",
                },
                "& .MuiDataGrid-columnSeparator, & .MuiDataGrid-sortIcon, & .MuiDataGrid-menuIconButton":
                  {
                    color: "#fff",
                  },
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: "#f6f9fc",
                },
                "& .MuiDataGrid-footerContainer": {
                  borderColor: "#d5e0eb",
                },
              }}
            />
          </Paper>
        </Box>

        <ReferentielAdd
          categories={categoryOptions}
          categoriesError={categoriesError}
          categoriesLoading={categoriesLoading}
          existingIds={existingIds}
          initialValues={editInitialValues}
          mode={formDialog.mode}
          onClose={closeFormDialog}
          onRefreshCategories={refreshCategories}
          onSaved={handleSaved}
          open={formDialog.open}
        />

        <Dialog
          fullWidth
          maxWidth="xs"
          onClose={() => !deleting && setDeleteTarget(null)}
          open={Boolean(deleteTarget)}
        >
          <DialogTitle>Supprimer le référentiel</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Confirmez-vous la suppression de « {deleteTarget?.code} —{" "}
              {deleteTarget?.name} » ?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              disabled={deleting}
              onClick={() => setDeleteTarget(null)}
            >
              Annuler
            </Button>
            <Button
              color="error"
              disabled={deleting}
              onClick={handleDelete}
              variant="contained"
            >
              {deleting ? (
                <CircularProgress color="inherit" size={20} />
              ) : (
                "Supprimer"
              )}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          autoHideDuration={4500}
          onClose={() =>
            setNotification((current) => ({ ...current, open: false }))
          }
          open={notification.open}
        >
          <Alert
            onClose={() =>
              setNotification((current) => ({ ...current, open: false }))
            }
            severity={notification.severity}
            variant="filled"
          >
            {notification.message}
          </Alert>
        </Snackbar>
      </>
    </SMQMainContainer>
  );
};

export default Referentiel;
